import {
  Table,
  Space,
  Button,
  Input,
  TablePaginationConfig,
  Statistic,
  Select,
  Divider,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import styles from "./TerminalCouplingTable.module.css";
import {
  getBusinessesThunk,
  setCurrentBusiness,
  selectTotalBusinessCount,
  selectLastUpdateKey,
  selectFilters,
  setFilters,
} from "../../../businesses/businessesSlice";
import { useNavigate } from "react-router-dom";
import { Empty } from "antd";
import { SearchOutlined, CopyrightOutlined } from "@ant-design/icons";

import Loader from "../../../common/loader/Loader";
import {
  BusinessType,
  IBusinessFull,
  IBusinessFullQuery,
} from "../../../../interfaces/businessFull.interface";
import { TableParams } from "../../../../interfaces/tableParams.interface";
import { IPagination } from "../../../../interfaces/pagination.interface";
import debounce from "lodash/debounce";
import TerminalBusinessAssigner from "../Terminal-Business-Assigner/TerminalBusinessAssigner";
import GeneralTableHeader from "../../../common/general-table-header/GeneralTableHeader";
import BusinessTerminalsList from "../Business-Terminals-List/BusinessTerminalsList";
import { selectUserPartnerId } from "../../../access-control/accountSlice";
// import SalesBusinessEditorModal from "../Sales-Business-Editor/SalesBusinessEditor";
// import SalesBusinessActivityDetails from "../Sales-Business-Activity-Details/SalesBusinessActivityDetails";

export default function TerminalCouplingTable() {
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [isEditorModalVisible, setIsEditorModalVisible] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const lastUpdateKey = useAppSelector(selectLastUpdateKey);
  const filters = useAppSelector(selectFilters);
  // const salesReps = useAppSelector(selectSalesReps);
  const [businessesInTable, setBusinessesInTable] = useState([]);
  const totalCount = useAppSelector(selectTotalBusinessCount);
  // const revenue = useAppSelector(selectKlearlyRevenue);
  const initialPagination = { current: 1, pageSize: 10 };
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: { ...initialPagination },
  });
  const partnerId = useAppSelector(selectUserPartnerId);
  // const [filters, setFilters] = useState<Partial<IBusinessFullQuery>>({});
  const dispatch = useAppDispatch();

  const fetchAllBusinesses = async (params: {
    pagination: IPagination;
    filters: Partial<IBusinessFullQuery>;
  }) => {
    setLoading(true);

    if (partnerId) {
      params.filters.partnerId = partnerId;
    }
    await dispatch(
      getBusinessesThunk({
        currentPage: params?.pagination?.current!,
        pageSize: params?.pagination?.pageSize!,
        ...params.filters,
      })
    )
      .then(({ payload }) => {
        //@ts-ignore
        let businesses = payload.data;
        //@ts-ignore
        let totalCount = payload.totalCount;
        let businessesWithKeys = businesses.map((b: IBusinessFull) => ({
          ...b,
          key: b._id,
        }));
        setBusinessesInTable(businessesWithKeys);

        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: totalCount,
          },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  useEffect(() => {
    return () => {
      dispatch(
        setFilters({
          name: "",
          ...initialPagination,
        })
      );
    };
  }, []);

  useEffect(() => {
    fetchAllBusinesses({
      pagination: {
        current: tableParams.pagination?.current || 1,
        pageSize: tableParams.pagination?.pageSize!,
      },
      filters: { ...filters, status: "verified" },
    });
  }, [JSON.stringify(tableParams), JSON.stringify(filters), lastUpdateKey]);

  const showBusinessTerminalsListModal = (business: IBusinessFull) => {
    dispatch(setCurrentBusiness(business));
    setIsDetailModalVisible(true);
  };
  const showBusinessEditorModal = (business: IBusinessFull) => {
    dispatch(setCurrentBusiness(business));
    setIsEditorModalVisible(true);
  };

  const handleSalesRepresentativeFilterChange = (value: string) => {
    dispatch(setFilters({ salesRepresentative: value, ...initialPagination }));
    setTableParams((prev) => {
      return { ...prev, pagination: { ...initialPagination } };
    });
  };

  const handleTypeFilterChange = (value: BusinessType) => {
    dispatch(setFilters({ businessType: value, ...initialPagination }));
    setTableParams((prev) => {
      return { ...prev, pagination: { ...initialPagination } };
    });
  };
  const debouncedNameFilterChange = useCallback(
    debounce((nextValue) => handleNameFilterChange(nextValue), 400),
    [] // will be created only once initially
  );

  const handleDebouncedNameFilterChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    debouncedNameFilterChange(event);
  };
  const handleNameFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value.toLowerCase();
    dispatch(
      setFilters({
        name: name.replace(/\s+/g, " ").trim(),
        ...initialPagination,
      })
    );
    setTableParams((prev) => {
      return { ...prev, pagination: { ...initialPagination } };
    });
  };
  const handlResetFilters = () => {
    dispatch(setFilters({}));
    setTableParams((prev) => {
      return { pagination: { ...initialPagination } };
    });
  };

  const handleTableChange = (
    pagination: TablePaginationConfig
    // filters: Record<string, FilterValue>,
    // sorter: SorterResult<IBucketDeal>
  ) => {
    setTableParams({
      pagination,
      // filters,
      // ...sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      // dispatch(emptyBucketDealsTable());
    }
  };

  const columns: ColumnsType<IBusinessFull> = [
    {
      title: "Business",
      className: styles.table_business_column,
      key: "businessDetails",
      render: (_: any, record: IBusinessFull) => (
        <span>
          <span key={record._id + "businessName"}>{record.businessName}</span>
          {record.businessName !== record.businessTradeName && (
            <span style={{ opacity: 0.7 }}>
              <br />
              <CopyrightOutlined /> {record.businessTradeName}
            </span>
          )}
        </span>
      ),
    },
    {
      title: "Email",
      key: "email",
      className: styles.table_column,
      render: (_: any, record: IBusinessFull) => (
        <span key={record._id + "email"}>{record.rootUser?.email}</span>
      ),
    },
    {
      title: "MID",
      key: "mid",
      className: styles.table_column,
      render: (_: any, record: IBusinessFull) => (
        <span key={record._id + "mid"}>{record.mid}</span>
      ),
    },
    {
      title: "",
      key: "details",
      className: styles.table_column,
      render: (_: any, record: IBusinessFull) => (
        <Space
          size="small"
          key={record._id}
          className={styles.tables_buttons_container}
        >
          <Button
            type="primary"
            onClick={async () => {
              showBusinessEditorModal(record);
            }}
          >
            Assign Terminal
          </Button>
          <Button
            type="link"
            onClick={() => {
              showBusinessTerminalsListModal(record);
            }}
          >
            View All
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.container} key="divTable">
      <GeneralTableHeader
        title="Couple Terminal To Merchant"
        description="Search for a business and couple it to the terminal using the serial
        number"
        onBack={() => {
          // handlResetFilters();
          navigate("/terminals/list");
        }}
        showBackButton
        imageUrl="https://www.whatspos.com/data/images/apk_icon_2465034596111010294.png"
        statisticsCount={totalCount}
        statisticsLabel="Amount of Merchants"
        statisticsLoading={loading}
      />

      <div className={styles.table_container}>
        <Input
          onChange={handleDebouncedNameFilterChange}
          placeholder="Business Name, Email, Kvk ..."
          value={filters.businessName}
          className={styles.searchInput}
          prefix={<SearchOutlined />}
        />
        {businessesInTable.length === 0 && !loading && (
          <div style={{ height: "300px" }} className="center">
            <Empty
              description={"No Business Found"}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            ></Empty>
          </div>
        )}

        {businessesInTable.length > 0 && !loading && (
          <Table
            dataSource={businessesInTable}
            pagination={tableParams.pagination}
            loading={loading}
            columns={columns}
            //@ts-ignore
            onChange={handleTableChange}
          />
        )}
        {loading && <Loader text="Fetching businesses from server"></Loader>}
      </div>
      {/* <SalesBusinessActivityDetails
        visible={isDetailModalVisible}
        setVisible={setIsDetailModalVisible}
      ></SalesBusinessActivityDetails> */}
      {isEditorModalVisible && (
        <TerminalBusinessAssigner
          visible={isEditorModalVisible}
          setVisible={setIsEditorModalVisible}
        ></TerminalBusinessAssigner>
      )}

      {isDetailModalVisible && (
        <BusinessTerminalsList
          visible={isDetailModalVisible}
          setVisible={setIsDetailModalVisible}
        ></BusinessTerminalsList>
      )}
    </div>
  );
}
